import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCustomerConfig } from "../../../dashboard-config";
import {
  integratedTractorsOptions,
  truckDriverAssignmentOptions,
  truckFuelPercentOptions,
} from "features/app/tractors/api";
import type { Tractor } from "features/app/tractors/api";
import cn from "classnames";
import Button from "components/button";
import LocationSearcher from "./location-searcher";
import { useState, useEffect } from "react";
import type { Dispatch, SetStateAction } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { AiFillExclamationCircle } from "react-icons/ai";
import { BiCommentError, BiRefresh } from "react-icons/bi";
import ReactSelect from "react-select";
import type { Suggest } from "react-geosuggest";
import { extractDrivers } from "./driver-fanout";
import { resolveTractorFuelLevelInGallons } from "./services";
import type { FuelPlan, FuelPlanFormData } from "./services";
import { useSubmitFuelPlan } from "./use-submit-fuel-plan";

interface SingleTractorFormValues {
  tractor: Tractor | null;
  tractorFuel: number;
  originLocation: Suggest | null;
  waypoints: { waypoint: Suggest | null }[];
  destinationLocation: Suggest | null;
  routeMode: "short" | "fast";
}

interface TractorOption {
  label: string;
  value: Tractor;
}

interface SingleTractorFormProps {
  setFuelPlan: Dispatch<SetStateAction<FuelPlan | null>>;
}

const SingleTractorForm = ({ setFuelPlan }: SingleTractorFormProps) => {
  const { user } = useAuth0();
  const customerCode = user!["https://ifuelsmart.com/company"];
  const defaultFuelLevelInGallons = getCustomerConfig(customerCode).defaultFuelLevelInGallons;
  const INITIAL_FORM_STATE: SingleTractorFormValues = {
    tractor: null,
    tractorFuel: defaultFuelLevelInGallons,
    originLocation: null,
    waypoints: [],
    destinationLocation: null,
    routeMode: "fast",
  };

  const [fuelPlanError, setFuelPlanError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState("Analyzing route one");
  const [fuelLoading, setFuelLoading] = useState(false);
  const queryClient = useQueryClient();
  const {
    data: tractors = [],
    isFetching: loading,
    refetch: getTractors,
    dataUpdatedAt,
  } = useQuery(integratedTractorsOptions(customerCode));
  const updatedAt = dataUpdatedAt ? new Date(dataUpdatedAt) : null;
  // The generate-fuel-plan handler: one intent in, a result out. All the
  // orchestration (timeout race, ops email, response normalization) lives there.
  const submitFuelPlan = useSubmitFuelPlan();
  const { handleSubmit, control, watch, setValue, reset, formState, getValues } = useForm<SingleTractorFormValues>({
    defaultValues: INITIAL_FORM_STATE,
  });
  const { isSubmitting } = formState;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "waypoints",
  });

  const [tractorWatch, tractorFuelWatch] = watch(["tractor", "tractorFuel"]);

  // fetch for fuel percent and driver assignment
  useEffect(() => {
    if (tractorWatch?.Integration?.Truck?.ID) {
      setFuelLoading(true);
      // Imperative fetchQuery (not useQuery) so this runs exactly once per truck
      // selection instead of refetching on focus and clobbering the form values.
      Promise.allSettled([
        queryClient.fetchQuery(
          truckFuelPercentOptions(customerCode, tractorWatch!.Integration!.Truck!.ID!)
        ),
        queryClient.fetchQuery(
          truckDriverAssignmentOptions(customerCode, tractorWatch!.Integration!.Truck!.ID!)
        ),
      ])
        .then(([fuelPercentResponse, driverAssignmentResponse]) => {
          // We will refactor at some point. The api returns fuellevelingallons but it actually percent
          const fuelLevelInPercent =
            fuelPercentResponse.status === "fulfilled" ? fuelPercentResponse.value.fuelLevelInPercentage : null;

          const driverAssignment = (driverAssignmentResponse.status === "fulfilled"
            ? driverAssignmentResponse.value.driverAssignment
            : null) as Parameters<typeof extractDrivers>[0];

          // A team-driven truck has more than one driver. Carry the full list on
          // the tractor object; the scalar fields stay the primary (first) driver
          // for backward compatibility with the rest of the form/send flow.
          const drivers = extractDrivers(driverAssignment);
          const primary = drivers[0] ?? { driver_id: null, driver_name: null, phoneNumber: null };
          setValue(
            "tractorFuel",
            resolveTractorFuelLevelInGallons({
              liveFuelLevelInPercent: fuelLevelInPercent,
              tankCapacityInGallons: tractorWatch!.tank_capacity,
              reportedFuelLevelInGallons: tractorWatch!.fuelLevelInGallons,
              customerDefaultInGallons: defaultFuelLevelInGallons,
            })
          );
          setValue("tractor", {
            ...tractorWatch!,
            // New tractor object only has Integration information related to the Truck.
            // Driver assignment is fetched separately and added to the tractor object.
            driver_id: primary.driver_id,
            driver_name: primary.driver_name,
            phoneNumber: primary.phoneNumber,
            drivers,
          });
          setFuelLoading(false);
        })
        .catch(() => {
          setFuelLoading(false);
        });
    }
  }, [tractorWatch?.Integration?.Truck?.ID, customerCode, defaultFuelLevelInGallons, setValue, queryClient]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (isSubmitting) {
      const messages = ["Analyzing route one", "Analyzing route two", "Analyzing route three"];
      let currentIndex = 0;

      const rotateMessage = () => {
        if (currentIndex < messages.length - 1) {
          currentIndex++;
          setLoadingMessage(messages[currentIndex]);
          timer = setTimeout(rotateMessage, 3000);
        }
      };

      timer = setTimeout(rotateMessage, 3000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isSubmitting]);

  // Fire the intent, then map the handler's result onto UI state. The component
  // owns the error banner and the plan hand-off; it does not know how a plan is
  // built. A timed-out or failed run surfaces nothing, matching prior behavior.
  const onSubmit = async (formData: SingleTractorFormValues) => {
    setFuelPlanError(null);
    // The form values overlap the handler's FuelPlanFormData at runtime (tractor,
    // located origin/destination/waypoints, routeMode); cast across the nominal gap.
    const result = await submitFuelPlan(formData as unknown as FuelPlanFormData);
    if (result.status === "success") {
      setFuelPlan(result.fuelPlan);
    } else if (result.status === "error") {
      setFuelPlanError(result.error);
    }
  };

  const handleRefreshTractor = () => {
    reset({ ...getValues(), tractor: null, tractorFuel: defaultFuelLevelInGallons });
    getTractors();
  };

  return (
    <form
      className="px-3 pt-4 pb-2 flex flex-col h-full flex-shrink-0 overflow-y-auto"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
    >
      {/* Alert */}
      {fuelPlanError && (
        <div
          className="bg-red-50 border-l-4 border-red-500 text-red-700 px-2 py-1.5 rounded relative mb-2 flex items-center text-xs"
          role="alert"
        >
          <BiCommentError className="mr-1 text-base flex-shrink-0" />
          <span className="block sm:inline">{fuelPlanError}</span>
        </div>
      )}

      {isSubmitting && (
        <div
          className="bg-blue-50 border-l-4 border-blue-500 text-blue-700 px-2 py-1.5 rounded relative mb-2 flex items-center text-xs"
          role="alert"
        >
          <AiFillExclamationCircle className="mr-1 text-base flex-shrink-0" />
          <span className="block sm:inline">{loadingMessage}</span>
        </div>
      )}

      <Controller
        name="tractor"
        control={control}
        rules={{ required: "Tractor is required" }}
        render={({ field, fieldState: { error } }) => {
          return (
            <div className="mb-4 relative">
              <div className="flex justify-between">
                <label className="block text-gray-700 text-sm font-bold mb-1" htmlFor="tractor">
                  Tractor
                </label>
                <span className="text-xs text-gray-500">
                  Updated{" "}
                  {updatedAt?.toLocaleString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </span>
              </div>
              <ReactSelect
                id="tractor"
                className="tractor"
                classNamePrefix="tractor-select"
                options={tractors.map(
                  (tractor): TractorOption => ({
                    label: tractor.unit_number,
                    value: tractor,
                  })
                )}
                onChange={(option) => {
                  console.log("selected tractor", option!.value);
                  field.onChange({
                    ...option!.value,
                  });
                  setValue(
                    "tractorFuel",
                    resolveTractorFuelLevelInGallons({
                      reportedFuelLevelInGallons: option!.value.fuelLevelInGallons,
                      customerDefaultInGallons: defaultFuelLevelInGallons,
                    })
                  );
                }}
                value={tractorWatch ? { label: tractorWatch.unit_number, value: tractorWatch } : null}
                placeholder="Select a tractor..."
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: "#ea580c",
                    primary25: "#ffedd5",
                  },
                })}
              />

              <BiRefresh
                className={cn(
                  "absolute text-base text-gray-500 top-9 right-12 cursor-pointer hover:text-orange-500 transition-colors",
                  {
                    "animate-spin text-orange-500": loading,
                  }
                )}
                onClick={handleRefreshTractor}
                title="Refresh tractors"
              />

              {error && (
                <div className="text-red-600 mt-0.5 flex items-center gap-1">
                  <AiFillExclamationCircle className="text-xs" />
                  <span className="text-xs">{error.message}</span>
                </div>
              )}
            </div>
          );
        }}
      />

      <Controller
        name="tractorFuel"
        control={control}
        render={({ field }) => (
          <div className="mb-4">
            <label className="block text-gray-700 text-xs font-medium mb-1">
              <div className="flex items-center gap-2">
                <span>
                  Tractor Fuel (
                  {tractorWatch
                    ? `${field.value} gal (${Math.round((field.value / tractorWatch.tank_capacity) * 100)}%)`
                    : "Select truck first"}
                  )
                </span>
                {fuelLoading && (
                  <div className="flex items-center gap-1 text-orange-600">
                    <svg
                      className="animate-spin h-3 w-3"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span className="text-xs">Fetching fuel level...</span>
                  </div>
                )}
              </div>
            </label>
            <div className="px-1">
              <input
                type="range"
                className="block w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-600 slider-progress"
                style={{
                  background: tractorWatch
                    ? `linear-gradient(to right, #ea580c ${
                        ((tractorFuelWatch - 15) / (tractorWatch.tank_capacity - 15)) * 100
                      }%, #e5e7eb ${((tractorFuelWatch - 15) / (tractorWatch.tank_capacity - 15)) * 100}%)`
                    : "#e5e7eb",
                }}
                min="15"
                max={tractorWatch?.tank_capacity}
                value={tractorFuelWatch ?? tractorWatch?.fuelLevelInGallons ?? defaultFuelLevelInGallons}
                onChange={(e) => {
                  field.onChange(e);
                  // Update the background fill when slider moves
                  e.target.style.background = tractorWatch
                    ? `linear-gradient(to right, #ea580c ${
                        ((Number(e.target.value) - 15) / (tractorWatch.tank_capacity - 15)) * 100
                      }%, #e5e7eb ${((Number(e.target.value) - 15) / (tractorWatch.tank_capacity - 15)) * 100}%)`
                    : "#e5e7eb";
                }}
                disabled={!tractorWatch || fuelLoading}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-0.5 px-1">
              <span>Min (15 gal)</span>
              <span>Max ({tractorWatch?.tank_capacity || "?"} gal)</span>
            </div>
          </div>
        )}
      />

      <Controller
        name="originLocation"
        control={control}
        rules={{ required: "Origin required" }}
        render={({ field, fieldState: { error } }) => (
          <LocationSearcher
            label="Origin"
            placeholder="Enter origin location"
            onSuggestSelect={field.onChange}
            error={error ? error.message : null}
            initialValue={field.value?.description}
            value={field.value?.description}
          />
        )}
      />

      {/* Via */}
      <button
        type="button"
        className={cn(
          "bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium py-1.5 px-3 rounded w-full mb-3 transition-all flex items-center justify-center",
          { "opacity-50 cursor-not-allowed": fields.length === 4 }
        )}
        onClick={() => append({ waypoint: null })}
        disabled={fields.length === 4}
      >
        <span className="mr-1">+</span> Add Stop
      </button>

      {fields.map((field, i) => (
        <div key={field.id} className="flex mb-3">
          <Controller
            name={`waypoints.${i}.waypoint`}
            control={control}
            rules={{ required: "Stop required" }}
            render={({ field, fieldState: { error } }) => {
              return (
                <LocationSearcher
                  label=""
                  inputClassName="text-sm h-8 py-1 px-2.5"
                  placeholder="Enter stop location"
                  onSuggestSelect={field.onChange}
                  error={error ? error.message : null}
                  initialValue={field.value?.description}
                  value={field.value?.description}
                />
              );
            }}
          />
          <div
            className="bg-red-500 hover:bg-red-600 text-white flex items-center justify-center h-8 mt-0 w-8 rounded-r cursor-pointer transition-colors"
            onClick={() => remove(i)}
            title="Remove stop"
          >
            <span>×</span>
          </div>
        </div>
      ))}

      {/* Destination */}
      <Controller
        name="destinationLocation"
        control={control}
        rules={{ required: "Destination required" }}
        render={({ field, fieldState: { error } }) => {
          return (
            <LocationSearcher
              label="Destination"
              labelClassName="text-xs font-medium mb-1 text-gray-700"
              inputClassName="text-sm h-8 py-1 px-2.5"
              placeholder="Enter destination location"
              onSuggestSelect={field.onChange}
              error={error ? error.message : null}
              initialValue={field.value?.description}
            />
          );
        }}
      />

        <div className="">
          <label className="block text-sm font-bold text-gray-700 mb-2">Route Mode</label>
          <div className="flex w-full rounded-md overflow-hidden border border-gray-200 shadow-sm">
            <button
              type="button"
              onClick={() => setValue("routeMode", "short")}
              className={`flex-1 py-1.5 text-xs font-medium transition-all ${
                watch("routeMode") === "short" ? "bg-gray-700 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              Short
            </button>
            <button
              type="button"
              onClick={() => setValue("routeMode", "fast")}
              className={`flex-1 py-1.5 text-xs font-medium transition-all ${
                watch("routeMode") === "fast" ? "bg-gray-700 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              Fast
            </button>
          </div>
        </div>

      {/* Actions */}
      <div className="flex space-x-2 mt-auto pt-3">
        <Button
          variant="lightGray"
          type="button"
          onClick={() => {
            reset(INITIAL_FORM_STATE);
          }}
          className="px-3 py-1.5 rounded shadow-sm hover:bg-gray-200 transition-colors font-medium text-xs"
        >
          Reset
        </Button>
        <button
          type="submit"
          className="bg-orange-600 hover:bg-orange-700 text-white font-medium py-1.5 px-3 rounded shadow-sm transition-colors w-full flex items-center justify-center text-xs"
        >
          {isSubmitting ? (
            <>
              <svg
                className="animate-spin mr-1.5 h-3.5 w-3.5 text-white inline-block"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Processing
            </>
          ) : (
            "Get Fuel Plan"
          )}
        </button>
      </div>
    </form>
  );
};

export default SingleTractorForm;
